import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Alert, Badge, Button, Form, Table, ProgressBar, Modal, Nav } from 'react-bootstrap';
import SectionContainer from '../../components/UI/SectionContainer';
import Spinner from '../../components/UI/Spinner';
import auditCapacityService from '../../services/auditCapacityService';

const AuditCapacityMonitoring = () => {
  const [loading, setLoading] = useState(true);
  const [capacityStatus, setCapacityStatus] = useState(null);
  const [capacityHistory, setCapacityHistory] = useState([]);
  const [alertHistory, setAlertHistory] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [retentionDays, setRetentionDays] = useState(90);
  const [error, setError] = useState(null);

  useEffect(() => {
    // 병렬로 API 호출하여 로딩 시간 단축
    Promise.all([
      fetchCapacityStatus(),
      fetchCapacityHistory(),
      fetchAlertHistory()
    ]);
  }, []);

  const fetchCapacityStatus = async () => {
    try {
      setLoading(true);
      const result = await auditCapacityService.getCapacityStatus();
      
      if (result.success) {
        setCapacityStatus(result.data);
        setError(null);
      } else {
        setError(result.message || '용량 상태 조회에 실패했습니다.');
      }
    } catch (err) {
      setError('용량 상태 조회에 실패했습니다: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCapacityHistory = async () => {
    try {
      // 모든 타겟의 히스토리를 조회 (최근 30개만 조회하여 성능 향상)
      const result = await auditCapacityService.getCapacityHistory(null, 30);
      
      if (result.success) {
        setCapacityHistory(Array.isArray(result.data) ? result.data : []);
      } else {
        console.error('용량 히스토리 조회 실패:', result.message);
      }
    } catch (err) {
      console.error('용량 히스토리 조회 실패:', err);
    }
  };

  const fetchAlertHistory = async () => {
    try {
      const result = await auditCapacityService.getAlertHistory(10);
      
      if (result.success) {
        setAlertHistory(Array.isArray(result.data) ? result.data : []);
      } else {
        console.error('경보 히스토리 조회 실패:', result.message);
      }
    } catch (err) {
      console.error('경보 히스토리 조회 실패:', err);
    }
  };

  const handleManualCheck = async () => {
    try {
      setLoading(true);
      const result = await auditCapacityService.executeManualCheck();
      
      if (result.success) {
        await fetchCapacityStatus();
        await fetchCapacityHistory();
      } else {
        setError(result.message || '수동 체크 실행에 실패했습니다.');
      }
    } catch (err) {
      setError('수동 체크 실행에 실패했습니다: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async () => {
    try {
      setLoading(true);
      const result = await auditCapacityService.executeArchive(retentionDays);
      
      if (result.success) {
        alert(`아카이빙 완료: ${JSON.stringify(result.data)}`);
        setArchiveDialogOpen(false);
        await fetchCapacityStatus();
      } else {
        alert(result.message || '아카이빙 실행에 실패했습니다.');
      }
    } catch (err) {
      alert('아카이빙 실행에 실패했습니다: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getHealthColor = (health) => {
    switch (health) {
      case 'healthy': return 'success';
      case 'warning': return 'warning';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  const getAlertLevelColor = (level) => {
    switch (level) {
      case 'normal': return 'success';
      case 'warning': return 'warning';
      case 'critical': return 'error';
      case 'emergency': return 'error';
      default: return 'default';
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading && !capacityStatus) {
    return (
      <SectionContainer title="감사 로그 용량 모니터링">
        <div className="text-center py-5">
          <Spinner />
        </div>
      </SectionContainer>
    );
  }

  return (
    <SectionContainer title="감사 로그 용량 모니터링">
      <Row className="mb-3 align-items-center">
        <Col>
          <h4>감사 로그 용량 모니터링</h4>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={fetchCapacityStatus}
            disabled={loading}
          >
            🔄 새로고침
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleManualCheck}
            disabled={loading}
          >
            ▶️ 수동 모니터링
          </Button>
          <Button
            variant="warning"
            size="sm"
            onClick={() => setArchiveDialogOpen(true)}
          >
            📦 아카이빙
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      {capacityStatus && (
        <>
          <Row className="mb-4">
            <Col md={3} sm={6} className="mb-3">
              <Card className={`h-100 border-${capacityStatus.overall_health === 'healthy' ? 'success' : capacityStatus.overall_health === 'warning' ? 'warning' : 'danger'}`}>
                <Card.Body className="text-center">
                  <div className="text-muted small mb-1">
                    {capacityStatus.overall_health === 'healthy' ? '✅' : capacityStatus.overall_health === 'warning' ? '⚠️' : '❌'} 전체 상태
                  </div>
                  <h2 className={`mb-0 text-${capacityStatus.overall_health === 'healthy' ? 'success' : capacityStatus.overall_health === 'warning' ? 'warning' : 'danger'}`}>
                    <Badge bg={capacityStatus.overall_health === 'healthy' ? 'success' : capacityStatus.overall_health === 'warning' ? 'warning' : 'danger'}>
                      {capacityStatus.overall_health?.toUpperCase() || 'UNKNOWN'}
                    </Badge>
                  </h2>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} sm={6} className="mb-3">
              <Card className="h-100 border-primary">
                <Card.Body className="text-center">
                  <div className="text-muted small mb-1">💾 전체 용량</div>
                  <h2 className="mb-0 text-primary">
                    {(capacityStatus.total_capacity_gb || 0).toFixed(2)} GB
                  </h2>
                  <small className="text-muted">
                    사용: {(capacityStatus.total_used_gb || 0).toFixed(2)} GB
                  </small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} sm={6} className="mb-3">
              <Card className="h-100 border-info">
                <Card.Body className="text-center">
                  <div className="text-muted small mb-1">📊 사용률</div>
                  <h2 className="mb-0 text-info">
                    {(capacityStatus.overall_usage_percentage || 0).toFixed(1)}%
                  </h2>
                  <ProgressBar
                    now={capacityStatus.overall_usage_percentage || 0}
                    variant={
                      (capacityStatus.overall_usage_percentage || 0) >= 90
                        ? 'danger'
                        : (capacityStatus.overall_usage_percentage || 0) >= 80
                        ? 'warning'
                        : 'primary'
                    }
                    className="mt-2"
                  />
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} sm={6} className="mb-3">
              <Card className="h-100 border-danger">
                <Card.Body className="text-center">
                  <div className="text-muted small mb-1">🚨 경보</div>
                  <h2 className="mb-0 text-danger">
                    {capacityStatus.critical_alerts || 0}
                  </h2>
                  <small className="text-muted">
                    경고: {capacityStatus.warning_alerts || 0}
                  </small>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(parseInt(k))} className="mb-3">
            <Nav.Item>
              <Nav.Link eventKey={0}>모니터링 대상</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey={1}>용량 히스토리</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey={2}>경보 히스토리</Nav.Link>
            </Nav.Item>
          </Nav>

          {activeTab === 0 && (
            <Row>
              {Object.entries(capacityStatus.targets || {}).map(([target, info]) => (
                <Col md={4} sm={12} className="mb-3" key={target}>
                  <Card className="h-100">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <strong>
                        {target === 'database' ? '💾 데이터베이스' : 
                         target === 'file_system' ? '📁 파일 시스템' : 
                         '📄 로그 파일'}
                      </strong>
                      <Badge bg={(info.alert_level || 'normal') === 'normal' ? 'success' : (info.alert_level || 'normal') === 'warning' ? 'warning' : 'danger'}>
                        {info.alert_level || 'unknown'}
                      </Badge>
                    </Card.Header>
                    <Card.Body>
                      <div className="mb-3">
                        <small className="text-muted">사용률</small>
                        <h4>{info.usage_percentage?.toFixed(1)}%</h4>
                        <ProgressBar
                          now={info.usage_percentage || 0}
                          variant={
                            (info.usage_percentage || 0) >= 90
                              ? 'danger'
                              : (info.usage_percentage || 0) >= 80
                              ? 'warning'
                              : 'primary'
                          }
                        />
                      </div>

                      {target === 'database' && (
                        <>
                          <p className="mb-1"><small>크기: {(info.total_size_mb || 0).toFixed(2)} MB</small></p>
                          <p className="mb-1"><small>테이블 수: {info.table_count || 0}</small></p>
                        </>
                      )}

                      {target === 'log_files' && (
                        <>
                          <p className="mb-1"><small>파일 수: {info.total_log_files || 0}</small></p>
                          <p className="mb-1"><small>총 크기: {(info.total_log_size_mb || 0).toFixed(2)} MB</small></p>
                        </>
                      )}

                      {info.recommendations && info.recommendations.length > 0 && (
                        <Alert variant="warning" className="mt-2 mb-0">
                          <small>{info.recommendations[0]}</small>
                        </Alert>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}

          {activeTab === 1 && (
            <Card>
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>대상</th>
                    <th>사용률</th>
                    <th>용량</th>
                    <th>사용량</th>
                    <th>경보 레벨</th>
                    <th>기록 시간</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(capacityHistory) && capacityHistory.length > 0 ? (
                    capacityHistory.map((record, index) => (
                      <tr key={index}>
                        <td>{record.target}</td>
                        <td>{parseFloat(record.usage_percentage || 0).toFixed(1)}%</td>
                        <td>{formatBytes(record.capacity_bytes)}</td>
                        <td>{formatBytes(record.used_bytes)}</td>
                        <td>
                          <Badge bg={record.alert_level === 'normal' ? 'success' : record.alert_level === 'warning' ? 'warning' : 'danger'}>
                            {record.alert_level}
                          </Badge>
                        </td>
                        <td>{record.recorded_at}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center text-muted">데이터가 없습니다</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card>
          )}

          {activeTab === 2 && (
            <Card>
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>대상</th>
                    <th>경보 레벨</th>
                    <th>사용률</th>
                    <th>임계값</th>
                    <th>알림 발송</th>
                    <th>발생 시간</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(alertHistory) && alertHistory.length > 0 ? (
                    alertHistory.map((alert, index) => (
                      <tr key={index}>
                        <td>{alert.target}</td>
                        <td>
                          <Badge bg={alert.alert_level === 'normal' ? 'success' : alert.alert_level === 'warning' ? 'warning' : 'danger'}>
                            {alert.alert_level}
                          </Badge>
                        </td>
                        <td>{parseFloat(alert.usage_percentage || 0).toFixed(1)}%</td>
                        <td>{parseFloat(alert.threshold_exceeded || 0).toFixed(0)}%</td>
                        <td>
                          {alert.notification_sent ? (
                            <span className="text-success">✅</span>
                          ) : (
                            <span className="text-danger">❌</span>
                          )}
                        </td>
                        <td>{alert.created_at}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center text-muted">데이터가 없습니다</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card>
          )}
        </>
      )}

      <Modal show={archiveDialogOpen} onHide={() => setArchiveDialogOpen(false)}>
        <Modal.Header closeButton>
          <Modal.Title>📦 감사 로그 아카이빙</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">
            지정한 보존 기간보다 오래된 감사 로그를 아카이빙합니다.
          </p>
          <Form.Group className="mb-3">
            <Form.Label>보존 기간 (일)</Form.Label>
            <Form.Control
              type="number"
              value={retentionDays}
              onChange={(e) => setRetentionDays(parseInt(e.target.value))}
            />
            <Form.Text className="text-muted">
              이 기간보다 오래된 로그가 아카이빙됩니다
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setArchiveDialogOpen(false)}>
            취소
          </Button>
          <Button variant="warning" onClick={handleArchive}>
            아카이빙 실행
          </Button>
        </Modal.Footer>
      </Modal>
    </SectionContainer>
  );
};

export default AuditCapacityMonitoring;
